import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, CardTitle, CardText, FormGroup, Label, Input, Button, Collapse, Spinner, Alert, Row, Col, InputGroup, InputGroupText } from 'reactstrap';
import { apiService } from '../../../services/ApiService';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../components/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS, EXTERNAL_URLS } from '../../../services/Endpoints';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const MAX_MONTO = 100000000; // 100 millones

const SimuladorCrecimiento = () => {
    const { t, i18n } = useTranslation('simulador');
    const API_KEY = import.meta.env.VITE_APP_API_KEY;
    const API_BASE = import.meta.env.VITE_APP_API_URL;
    const [monto, setMonto] = useState('');
    const [plazos, setPlazos] = useState([]);
    const [plazoSeleccionado, setPlazoSeleccionado] = useState('');
    const [resultado, setResultado] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [sending, setSending] = useState(false);
    const [mensajeSolicitud, setMensajeSolicitud] = useState('');
    const [downloadSuccess, setDownloadSuccess] = useState(false);
    const currentLang = i18n.language?.startsWith('en') ? 'en' : 'es';

    const location = useLocation();
    const { accessToken } = useAuth?.() || {};

    // ¿Simulador público (sin sesión) o privado?
    const isPublicSimulador = location.pathname.startsWith('/auth/');

    // Token bruto (de contexto o localStorage)
    const rawToken = accessToken || localStorage.getItem('token');

    // Solo consideramos autenticado si NO estamos en /auth/*
    const isAuthenticated = !isPublicSimulador && !!rawToken;


    // Cargar los plazos desde el endpoint al montar
    useEffect(() => {
        const fetchPlazos = async () => {
            try {
                const res = await apiService.getX('GETCATALOGOPLAZO', import.meta.env.VITE_APP_API_KEY);
                console.log('GETCATALOGOPLAZO response:', res);
                if (res?.succeeded && Array.isArray(res.data)) {
                    setPlazos(res.data);
                } else {
                    console.warn('Catálogo de plazos no válido:', res);
                    setPlazos([]);
                }
            } catch (err) {
                console.error('Error cargando plazos:', err);
            }
        };
        fetchPlazos();
    }, []);


    const handleMontoChange = (e) => {
        let rawValue = e.target.value;

        // 1. LIMPIEZA INICIAL Y NORMALIZACIÓN DECIMAL:
        // Eliminar las comas (',') si se usan como separadores de miles
        let cleanedValue = rawValue.replace(/,/g, '');

        // 2. CONVERSIÓN DE COMA A PUNTO DECIMAL:
        // Si el usuario presiona coma, la tratamos como punto decimal.
        // Esto es crucial para que funcione si la usa como separador decimal.
        cleanedValue = cleanedValue.replace(/,/g, '.');

        // 3. FILTRADO: Permitir solo dígitos y el punto decimal.
        cleanedValue = cleanedValue.replace(/[^\d.]/g, '');

        // 4. ASEGURAR ÚNICO PUNTO DECIMAL: Si hay múltiples puntos.
        const parts = cleanedValue.split('.');
        if (parts.length > 2) {
            // Mantiene la primera parte, el punto, y concatena el resto.
            cleanedValue = parts[0] + '.' + parts.slice(1).join('');
        }

        // Si la cadena está vacía (borrado)
        if (cleanedValue === '') {
            setMonto('');
            return;
        }

        // 5. VALIDACIÓN DE FORMATO
        // Permite hasta 9 dígitos antes del punto, hasta 2 después (tu regex original)
        const finalRegex = /^\d{0,9}(\.\d{0,2})?$/;

        if (!finalRegex.test(cleanedValue)) {
            // Si no cumple el formato, no actualizamos el estado.
            return;
        }

        const numeric = parseFloat(cleanedValue);

        if (!Number.isNaN(numeric) && numeric <= MAX_MONTO) {
            // 6. Guardamos el valor limpio (con punto decimal) en el estado `monto`
            setMonto(cleanedValue);
        }
    };

    const formatMontoInput = (value) => {
        if (!value) return '';

        // Convertir el valor a String y separarlo en parte entera y decimal.
        const [integerPart, decimalPart] = String(value).split('.');

        // Usar 'en-US' para obtener la coma como separador de miles
        const formattedInteger = Number(integerPart).toLocaleString('en-US', {
            maximumFractionDigits: 0
        });

        let output = formattedInteger;

        // Si el valor original termina en punto (ej: '1234.') o tiene decimales, 
        // añadimos el punto y la parte decimal.
        if (String(value).endsWith('.')) {
            output += '.';
        } else if (decimalPart !== undefined) {
            output += '.' + decimalPart;
        }

        return output;
    };

    // Llamada al endpoint de simulación
    const handleCalcular = async () => {
        setError('');
        setResultado(null);

        if (!monto || !plazoSeleccionado) {
            // setError('Debes ingresar un monto y seleccionar un plazo');
            setError(t('errorMissingFields'));
            return;
        }
        const montoFloat = parseFloat(monto);


        if (montoFloat < 1000) {
            setError(t('errorMinimumAmount') || 'El monto a invertir debe ser de al menos $1,000.00 USD.');
            return;
        }
        if (montoFloat > 100000000) {
            setError(
                t('errorMaximumAmount') ||
                'El monto máximo permitido es USD 100,000,000.00.'
            );
            return;
        }

        setLoading(true);
        try {
            const payload = {
                monto: montoFloat,
                plazoMeses: parseInt(plazoSeleccionado),
            };
            const res = await apiService.postX('CALCULARSIMULACION', API_KEY, payload);
            console.log('CALCULARSIMULACION postX response:', res);
            if (res?.succeeded) {
                setResultado(res.data); // el backend devuelve el resultado en `data`
            } else {
                // setError(res?.message || 'Error calculando la simulación');
                setError(res?.message || t('errorSimulationGeneric'));
            }
        } catch (err) {
            console.error('Error en simulación:', err);
            // setError('Error de conexión o respuesta inválida');
            setError(t('errorSimulationNetwork'));
        } finally {
            setLoading(false);
        }
    };
    // Llamada al endpoint para descargar el PDF de proyección
    const handleDescargarPDF = async () => {
        setError('');
        setDownloadSuccess(false);
        if (!monto || !plazoSeleccionado) {
            setError(t('errorMissingFields'));
            return;
        }

        const montoFloat = parseFloat(monto);

        if (montoFloat < 1000) {
            setError(t('errorMinimumAmount') || 'El monto a invertir debe ser de al menos $1,000.00 USD.');
            return;
        }
        if (montoFloat > 100000000) {
            setError(
                t('errorMaximumAmount') ||
                'El monto máximo permitido es USD 100,000,000.00.'
            );
            return;
        }
        const endpointPath = API_ENDPOINTS.POST_PROYECCION_PDF(currentLang);
        const url = `${API_BASE}${endpointPath}`; // Construcción correcta de la URL
        const payload = {
            monto: montoFloat,
            plazoMeses: parseInt(plazoSeleccionado),
        };

        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-api-key': API_KEY,
            Authorization: `Bearer ${rawToken}`
        };

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(payload)
            });

            // El manejo de errores para archivos debe ser diferente, 
            // ya que el error body puede no ser un JSON.

            if (!response.ok) {
                let errorMessage = 'Error al generar el PDF de proyección. Por favor, inténtalo de nuevo.';

                // Intenta leer el cuerpo de la respuesta como texto para un mensaje de error
                try {
                    const errorText = await response.text();
                    // Si el error es un JSON válido y tiene un mensaje, úsalo.
                    const errorJson = JSON.parse(errorText);
                    errorMessage = errorJson.message || errorMessage;
                } catch {
                    // Si falla al parsear o no es JSON, usa el mensaje por defecto o el status.
                    errorMessage = `Error ${response.status}: ${response.statusText}`;
                }

                throw new Error(errorMessage);
            }

            // --- FIN: Corrección de la URL de Endpoint ---

            // Obtener el Blob de la respuesta (para archivos)
            const blob = await response.blob();

            // Crear una URL para el Blob
            const blobUrl = window.URL.createObjectURL(blob); // Usar blobUrl para evitar confusión con 'url'

            // Crear un enlace temporal para la descarga
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = `proyeccion-inversion-${montoFloat}-${plazoSeleccionado}meses.pdf`;
            document.body.appendChild(a);
            a.click();

            // Limpiar
            a.remove();
            window.URL.revokeObjectURL(blobUrl);
            setDownloadSuccess(true);
            // Ocultar el alert de éxito después de 5 segundos
            setTimeout(() => setDownloadSuccess(false), 5000);
        } catch (error) {
            console.error('Error al descargar el PDF:', error);
            setError(error.message || 'Ocurrió un error al intentar descargar el PDF.');
            setDownloadSuccess(false);
        }
    };

    // Helpers de formateo
    const fmtUSD = (n) =>
        typeof n === 'number'
            ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })
            : n ?? '-';

    const fmtPct = (n) =>
        typeof n === 'number'
            ? `${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}%`
            : n ?? '-';

    // Prepara datos para el gráfico combinando ambos cronogramas si existen
    const buildChartData = (res) => {
        const perArr = res?.periodicaMensual?.cronograma || [];
        const fijaArr = res?.fija?.cronograma || [];
        const len = Math.max(perArr.length, fijaArr.length);
        const rows = [];
        for (let i = 0; i < len; i++) {
            rows.push({
                mes: (perArr[i]?.periodo ?? fijaArr[i]?.periodo ?? i + 1),
                periodica: perArr[i]?.capitalMasRenta ?? null,
                fija: fijaArr[i]?.capitalMasRenta ?? null,
            });
        }
        return rows;



    };
    const handleSolicitar = async () => {
        setMensajeSolicitud('');

        // Validación básica
        if (!monto || !plazoSeleccionado) {
            // setMensajeSolicitud('Primero calcula una simulación antes de solicitar.');
            setMensajeSolicitud(t('msgRequestNeedSim'));
            return;
        }

        //  SIN SESIÓN → abrir URL externa en otra pestaña
        if (!isAuthenticated) {
            const urlPublica = EXTERNAL_URLS.SIMULADOR_PUBLIC_URL;
            window.open(urlPublica, '_blank', 'noopener,noreferrer');
            return;
        }

        // CON SESIÓN → POST interno con Bearer token
        const token = rawToken;
        if (!token) {
            // setMensajeSolicitud('Tu sesión ha expirado. Vuelve a iniciar sesión para continuar.');
            setMensajeSolicitud(t('msgSessionExpired'));
            return;
        }

        const payload = {
            monto: parseFloat(monto),
            plazo: parseInt(plazoSeleccionado, 10),
        };

        try {
            setSending(true);

            const url = `${API_BASE}${API_ENDPOINTS.POST_SOLICITUD_SIMULADOR_AUTH(currentLang)}`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            let data = null;
            try {
                data = await response.json();
            } catch {
                // setMensajeSolicitud('Respuesta no válida del servidor.');
                setMensajeSolicitud(t('msgInvalidResponse'));
                return;
            }

            // Manejo según las estructuras de la respuesta:
            if (data?.succeeded) {
                // Caso: tarea creada correctamente
                setMensajeSolicitud(
                    data.message ||
                    t('msgRequestOkFallback')
                    // 'La solicitud ha sido registrada correctamente. Un asesor se pondrá en contacto contigo.'
                );
            } else {
                // false: puede ser pendiente, cliente no encontrado, no autorizado, etc.
                setMensajeSolicitud(
                    data?.message ||
                    // 'No se pudo registrar la solicitud. Inténtalo nuevamente.'
                    t('msgRequestErrorFallback')
                );
            }
        } catch (e) {
            console.error('Error al enviar la solicitud desde el simulador:', e);
            // setMensajeSolicitud('Error de conexión al enviar la solicitud.');
            setMensajeSolicitud(t('msgRequestNetworkError'));
        } finally {
            setSending(false);
        }
    };

    // Derivados seguros a partir de `resultado` para usar en el render
    const entrada = resultado?.entrada ?? {};
    const fija = resultado?.fija ?? {};
    const resumenFija = fija?.resumen ?? {};

    const tasafija = resultado?.tasas?.fijaTotal ?? {};
    const tasaperiodica = resultado?.tasas?.periodicaTotal ?? {};

    const valorProyectadoALiquidarFija = fija?.resumen?.valorProyectadoALiquidar ?? {};
    const valorProyectadoALiquidarPeriodica = resultado?.periodicaMensual?.resumen?.valorProyectadoALiquidar ?? {};
    const per = resultado?.periodicaMensual ?? {};
    const resumenPer = per?.resumen ?? {};

    return (
        <Card className="my-5 shadow-sm">
            <CardHeader className="text-center">
                <CardTitle
                    tag="h4"
                    className="mb-2 fw-bold"
                    style={{ fontSize: '2.5rem' }}
                >
                    {/* Proyecta tu crecimiento */}
                    {t('title')}
                </CardTitle>
                <CardText
                    className="text-muted small mx-auto"
                    style={{ maxWidth: '520px', fontSize: '1rem' }}
                >
                    {t('subtitle')}
                    {/* Ingresa un monto y selecciona el plazo para conocer cómo podría crecer tu capital
                    con nuestro acompañamiento. Los resultados son estimados y te orientan
                    para planificar mejor tu futuro financiero. */}
                </CardText>
            </CardHeader>

            <CardBody>
                <Row className="justify-content-center">
                    <Col xs="12" md="8" lg="6">
                        {/* Monto y Plazo en 2 columnas en md+ */}
                        <div className="row g-3">

                            <div className="col-12 col-md-6">
                                <FormGroup>
                                    <Label for="monto" className="fw-semibold">
                                        {t('labelAmount')}
                                    </Label>
                                    <InputGroup>
                                        <InputGroupText>USD</InputGroupText>
                                        <Input
                                            id="monto"
                                            type="text"
                                            // Ajustar placeholder al formato Americano: Coma (,) para miles, Punto (.) para decimales
                                            placeholder="1,000.00"
                                            value={formatMontoInput(monto)} // Usa la función corregida
                                            onChange={handleMontoChange} // Usa la función corregida
                                        />
                                    </InputGroup>
                                    <span className="text-muted ms-1 fw-normal" style={{ fontSize: '0.7em' }}>
                                        (Mín. USD 1,000.00)
                                    </span>
                                </FormGroup>
                            </div>
                            <div className="col-12 col-md-6">
                                <FormGroup>
                                    <Label for="plazo" className="fw-semibold">
                                        {/* Selecciona el plazo en meses */}
                                        {t('labelTerm')}
                                    </Label>
                                    <Input
                                        type="select"
                                        id="plazo"
                                        value={plazoSeleccionado}
                                        onChange={(e) => setPlazoSeleccionado(e.target.value)}
                                    >
                                        <option value="">{t('selectTermPlaceholder')}</option>
                                        {plazos.map((p) => (
                                            <option key={p} value={p}>
                                                {/* {p} meses */}
                                                {p} {t('selectTermSuffix')}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Botón calcular centrado y ancho */}
                <div className="d-flex justify-content-center mt-2">
                    <Button
                        color="primary"
                        onClick={handleCalcular}
                        disabled={loading}
                        style={{ minWidth: '220px' }}
                    >
                        {loading ? <Spinner size="sm" className="me-2" /> : null}
                        {/* {loading ? 'Calculando...' : 'Calcular'} */}
                        {loading ? t('btnCalculating') : t('btnCalculate')}
                    </Button>
                </div>

                {/* Mensaje de error */}
                {error && (
                    <Alert color="danger" className="mt-3">
                        {error}
                    </Alert>
                )}

                {/* Resultados */}
                {resultado && (
                    <Collapse isOpen className="mt-4">
                        <Card className="shadow-sm">
                            <CardHeader className=" text-center">
                                <CardTitle
                                    tag="h5" // Hacemos el título de la CardHeader un poco más grande
                                    className="mb-1 text-primary fw-bold"
                                    style={{ fontSize: '1.5rem' }}
                                >
                                    {/* Observa cómo podría evolucionar tu capital */}
                                    {t('resultTitle')}
                                </CardTitle>

                                {/* Usamos un div con Row y Col para mejorar el layout y el tamaño del texto. */}
                                <div className="row justify-content-center text-center mt-3 mb-0">

                                    {/* Columna para MONTO */}
                                    <div className="col-6 col-sm-auto px-2 d-flex flex-column align-items-center">
                                        <p className="text-muted small mb-0 text-nowrap"> {/* Agregamos text-nowrap aquí */}
                                            {/* Monto Invertido */}
                                            {t('summaryAmountLabel')}
                                        </p>
                                        <h5 className="fw-bold mb-0 text-dark text-nowrap"> {/* Agregamos text-nowrap aquí también */}
                                            {fmtUSD(
                                                typeof entrada.monto === 'number'
                                                    ? entrada.monto
                                                    : parseFloat(monto) || 0
                                            )}
                                        </h5>
                                    </div>

                                    {/* Separador vertical sutil en móviles y tabletas */}
                                    <div className="d-block d-sm-none w-100 my-2"></div>

                                    {/* Columna para PLAZO */}
                                    <div className="col-6 col-sm-auto px-2 border-start d-none d-sm-block d-flex flex-column align-items-center">
                                        {/* El border-start crea una línea divisoria vertical en sm+ */}
                                        <p className="text-muted small mb-0 text-nowrap"> {/* Agregamos text-nowrap */}
                                            {/* Plazo Seleccionado */}
                                            {t('summaryTermLabel')}
                                        </p>
                                        <h5 className="fw-bold mb-0 text-dark text-nowrap"> {/* Agregamos text-nowrap */}
                                            {typeof entrada.plazoMeses === 'number'
                                                ? entrada.plazoMeses
                                                : plazoSeleccionado}{' '}
                                            {/* meses */}
                                            {t('monthsSuffix')}
                                        </h5>
                                    </div>

                                    {/* Columna para PLAZO (Alternativa para móviles sin border-start) */}
                                    <div className="col-6 col-sm-auto px-2 d-sm-none d-flex flex-column align-items-center">
                                        <p className="text-muted small mb-0 text-nowrap"> {/* Agregamos text-nowrap */}
                                            {/* Plazo Seleccionado */}
                                            {t('summaryTermLabel')}
                                        </p>
                                        <h5 className="fw-bold mb-0 text-dark text-nowrap"> {/* Agregamos text-nowrap */}
                                            {typeof entrada.plazoMeses === 'number'
                                                ? entrada.plazoMeses
                                                : plazoSeleccionado}{' '}
                                            {/* meses */}
                                            {t('monthsSuffix')}
                                        </h5>
                                    </div>

                                </div>
                            </CardHeader>

                            <CardBody>
                                {/* Subcards Resumen: Renta Periódica & Renta Fija */}
                                <div className="row g-3 mb-3">
                                    {/* Renta Periódica */}
                                    <div className="col-12 col-lg-6">
                                        <div className="p-3 rounded border h-100">
                                            <div className="fw-semibold mb-2 text-primary">
                                                {/* Renta periódica */}
                                                {t('cardPeriodicTitle')}
                                            </div>
                                            <div className="d-flex justify-content-between small">
                                                <span className="text-muted">
                                                    {/* Tasa Periódica */}
                                                    {t('fieldPeriodicRate')}
                                                </span>
                                                <span className="fw-semibold">
                                                    {fmtPct(tasaperiodica)}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between small mt-1">
                                                <span className="text-muted">{/*Renta acumulada*/}{t('fieldMonthlyRent')}</span>
                                                <span className="fw-semibold">
                                                    {fmtUSD(
                                                        typeof resumenPer.rentaMensual === 'number'
                                                            ? resumenPer.rentaMensual : "N/A"
                                                    )}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between small mt-1">
                                                <span className="text-muted">{/*Capital final*/}{t('fieldFinalCapital')}</span>
                                                <span className="fw-semibold">
                                                    {fmtUSD(valorProyectadoALiquidarPeriodica)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Renta Fija */}
                                    <div className="col-12 col-lg-6">
                                        <div className="p-3 rounded border  h-100">
                                            <div className="fw-semibold mb-2 text-primary">
                                                {/* Renta fija */}
                                                {t('cardFixedTitle')}
                                            </div>
                                            <div className="d-flex justify-content-between small">
                                                <span className="text-muted">{/*Tasa Fija*/}{t('fieldFixedRate')}</span>
                                                <span className="fw-semibold">
                                                    {fmtPct(tasafija)}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between small mt-1">
                                                <span className="text-muted">{/*Renta acumulada*/}{t('fieldAccumulatedRent')}</span>
                                                <span className="fw-semibold">
                                                    {fmtUSD(
                                                        typeof resumenFija.rentaNetaTotal === 'number'
                                                            ? resumenFija.rentaNetaTotal
                                                            : resumenFija.totalRentaPeriodo
                                                    )}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between small mt-1">
                                                <span className="text-muted">{/*Capital final*/}{t('fieldFinalCapital')}</span>
                                                <span className="fw-semibold">
                                                    {fmtUSD(valorProyectadoALiquidarFija)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Gráfico comparativo */}
                                <div className="mt-2">
                                    <div className="p-2 rounded border">
                                        <div className="fw-semibold mb-2">
                                            {/* Proyección de capital + renta */} {t('chartTitle')}
                                        </div>
                                        <div style={{ width: '100%', height: 320 }}>
                                            <ResponsiveContainer>
                                                <LineChart data={buildChartData(resultado)}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="mes" />
                                                    <YAxis
                                                        domain={[
                                                            (dataMin) =>
                                                                typeof dataMin === 'number'
                                                                    ? dataMin * 0.99
                                                                    : dataMin,
                                                            'auto',
                                                        ]}
                                                        tickFormatter={(v) =>
                                                            typeof v === 'number'
                                                                ? v.toLocaleString('en-US', {
                                                                    notation: 'compact',
                                                                    maximumFractionDigits: 1,
                                                                })
                                                                : v
                                                        }
                                                    />
                                                    <Tooltip
                                                        formatter={(val) =>
                                                            fmtUSD(
                                                                typeof val === 'number'
                                                                    ? val
                                                                    : Number(val)
                                                            )
                                                        }
                                                    />
                                                    <Legend />
                                                    <Line
                                                        type="monotone"
                                                        dataKey="periodica"
                                                        name={t('simulatorChartLegendPeriodic', 'Renta periódica')}
                                                        dot={false}
                                                        stroke="#0d6efd"
                                                        strokeWidth={2}
                                                        strokeDasharray="4 3"
                                                    />
                                                    <Line
                                                        type="monotone"
                                                        dataKey="fija"
                                                        name={t('simulatorChartLegendFixed', 'Renta fija')}
                                                        dot={false}
                                                        stroke="#e83e8c"
                                                        strokeWidth={2}
                                                    />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                </div>

                                {/* Botones Descargar PDF y Solicitar juntos y centrados */}
                                <div className="mt-4 d-flex flex-column flex-md-row justify-content-center gap-3 align-items-center">
                                    {/* Botón Descargar PDF (solo si el usuario está autenticado) */}
                                    {isAuthenticated && (
                                        <Button
                                            color="secondary"
                                            onClick={handleDescargarPDF}
                                            disabled={loading || sending || !resultado} // Deshabilitar si se está calculando, enviando o no hay resultados
                                            style={{ minWidth: '220px' }}
                                        >
                                            <i className="bi bi-download me-2" />
                                            {t('btnDownloadPDF')}
                                        </Button>
                                    )}

                                    {/* Botón Solicitar */}
                                    <Button
                                        color="primary"
                                        onClick={handleSolicitar}
                                        disabled={sending}
                                        style={{ minWidth: '220px' }}
                                    >
                                        {sending ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                {t('btnRequestSending')}
                                            </>
                                        ) : (
                                            t('btnRequest')
                                        )}
                                    </Button>
                                </div>


                                {mensajeSolicitud && (
                                    // <div className="mt-3 text-center w-">
                                    <div className="mt-4 d-flex justify-content-center">
                                        <Alert>
                                            <small className="text-muted">{mensajeSolicitud}</small>
                                        </Alert>
                                    </div>
                                )}
                                {downloadSuccess && (
                                    <div className="mt-4 d-flex justify-content-center">
                                        <Alert color="success" className="w-100 w-md-75 text-center" toggle={() => setDownloadSuccess(false)}>
                                            <i className="bi bi-check-circle-fill me-2" />
                                            {t('msgPDFDownloadSuccess') || '¡Descarga completada! Revisa tu carpeta de descargas para ver la proyección.'}
                                        </Alert>
                                    </div>
                                )}

                            </CardBody>
                        </Card>
                    </Collapse>
                )}
            </CardBody>
        </Card>
    );
};
export default SimuladorCrecimiento;
